import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';

import { useEditVolunteerEvent } from '@/api/hooks/volunteerHooks';
import { VolunteerEvent } from '@/api/models/volunteerModels';
import FormWrapper from '@/components/forms/FormWrapper';
import VolunteerEventForm from '@/components/forms/VolunteerEventForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { VolunteerEventData } from '@/lib/validations/VolunteersSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { FileToUpload, uploadMultipleToFireStorage } from '@/utils/uploadMultipleToFireStorage';

const EditVolunteerEvent = () => {
  const { volunteerEventJson } = useLocalSearchParams<{ volunteerEventJson: string }>();

  const volunteerEvent = safeJsonParse<VolunteerEvent>(volunteerEventJson);

  const { mutateAsync: editvolunteerEvent, isPending } = useEditVolunteerEvent();
  const { showSuccess, showError } = useEndFlowModal();

  const [fileProgress, setFileProgress] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data: VolunteerEventData) => {
    try {
      setIsLoading(true);
      let filesToUpload: FileToUpload[] = [];
      if (data.thumbnail) {
        data.thumbnail.map((thumb, index) => {
          if (thumb) {
            filesToUpload.push({
              uri: thumb,
              name: `thumb_${index}.${new Date().toISOString()}`
            });
          }
        });
      }
      if (filesToUpload.length > 0) {
        await uploadMultipleToFireStorage(
          `non-profits-orgs/${volunteerEvent?.nonProfitOrgId}/volunteerEvent/${volunteerEvent?.id}/thumbs/`,
          filesToUpload,
          (_, progress) => {
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          const mappedFiles = storagedFiles.map((result) => result.metadata.fullPath);
          editvolunteerEvent(
            {
              volunteerId: volunteerEvent?.id ?? '',
              body: {
                name: data.name,
                subtitle: data.name,
                description: data.description,
                address: data.address,
                thumbnail: mappedFiles,
                startDate: data.startDate?.toString(),
                location: data.location
              }
            },
            {
              onSuccess: () => {
                showSuccess({
                  title: i18n.t('profile.create_form.event_edit_success_message'),
                  message: ''
                });
                router.back();
              },
              onError: (e) => {
                console.error(e);
                setIsLoading(false);
              }
            }
          );
        });
      } else if (data.thumbnail && data?.thumbnail?.length > 0) {
        editvolunteerEvent(
          {
            volunteerId: volunteerEvent?.id ?? '',
            body: {
              name: data.name,
              subtitle: data.name,
              description: data.description,
              address: data.address,
              startDate: data.startDate?.toString(),
              location: data.location
            }
          },
          {
            onSuccess: () => {
              showSuccess({ title: i18n.t('profile.create_form.event_success_message'), message: '' });
              router.back();
            },
            onError: (e) => {
              console.error(e);
              setIsLoading(false);
            }
          }
        );
      } else {
        setIsLoading(false);
        showError({ title: 'Image Required', message: 'At least the main image is required' });
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  return (
    <FormWrapper>
      <VolunteerEventForm
        initialData={volunteerEvent}
        isLoading={isPending || isLoading}
        fileProgress={fileProgress}
        onSubmitTapped={onSubmit}
      />
    </FormWrapper>
  );
};

export default EditVolunteerEvent;
